import { useAuth, useUser } from '@clerk/clerk-react';
import * as Sentry from '@sentry/browser';
import mixpanel from 'mixpanel-browser';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useNotification from '~/hooks/use-notification';
import api, { setInterceptors, setOrganizationHeaders, unsetAuthenticationHeaders, unsetInterceptors } from '~/lib/api';
import { organizationEventsFromApi, userEventsFromApi } from '~/lib/data-parsers/event-flags';
import { fromApi as featureFlagsFromApi } from '~/lib/data-parsers/feature-flags';

const TEN_SECONDS_IN_MS = 10 * 1000;
const FIVE_SECONDS_IN_MS = 5 * 1000;

const fromApi = organization => ({
  id: organization.id,
  name: organization.name ?? organization.entity,
  featureFlags: featureFlagsFromApi(organization.feature_flags),
  eventFlags: organizationEventsFromApi(organization.event_flags),
});

// Runs on the first render. An effect would be too late, children would already have fetched data
// for the wrong organization.
const getInitialAssumedOrganizationId = () =>
  Number(new URLSearchParams(window.location.search).get('org_id')) ||
  Number(window.localStorage.getItem('onomondo-organization-id')) ||
  null;

const AuthenticationContext = createContext();

function AuthenticationProvider(props) {
  const history = useHistory();
  const { getToken: clerkGetToken, isSignedIn, signOut, isLoaded } = useAuth();
  const { user: clerkUser } = useUser();

  const { addNotification } = useNotification();
  const [email, setEmail] = useState(null);
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);
  const [canManageElevatedPermissions, setCanManageElevatedPermissions] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);
  const [writeTags, setWriteTags] = useState(true);
  const [readTags, setReadTags] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [assumedOrganizationId, setAssumedOrganizationId] = useState(getInitialAssumedOrganizationId);
  const [isViewingAsAssumedOrganization, setIsViewingAsAssumedOrganization] = useState(false);
  const [usersOrganizations, setUsersOrganizations] = useState([]);
  const [usersCurrentOrganizationId, setUsersCurrentOrganizationId] = useState(null);
  const [isRequestAccessAvailable, setIsRequestAccessAvailable] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const logout = useCallback(
    async e => {
      if (!isSignedIn) return;

      await signOut();
      mixpanel.track('Log out');
      if (mixpanel.get_property('$user_id')) mixpanel.reset();

      const isIntercomDefined = typeof window.Intercom === 'function';
      if (isIntercomDefined) window.Intercom('shutdown');
      unsetAuthenticationHeaders();
      unsetInterceptors();
      window.localStorage.removeItem('x-onomondo-org-id');
      setAssumedOrganizationId(null);
      setEmail(null);
      setIsMfaEnabled(false);
      setCanManageElevatedPermissions(false);
      setUserType(null);
      setUserId(null);
      setAccessLevel(null);

      let message = 'You have been logged out';
      if (e) {
        message = 'You have been logged out due to an error, the team has been notified';
        Sentry.captureException(e);
      }

      // forcing theme light is needed here since the login page is always in light mode now
      // and this way we make sure that the notification is also in light mode here
      addNotification({ message, type: e ? 'error' : null, forcedTheme: 'light' });
    },
    [addNotification, signOut, isSignedIn],
  );

  const getMe = useCallback(
    async userHasSwitchedOrgId => {
      try {
        const { data } = await api.get('/me');
        const events = userEventsFromApi(data.events);
        const accessLevel = data.type;
        const userType = data.user_type;
        setWriteTags(data.tags.filter(t => t.can_write));
        setReadTags(data.tags.filter(t => !t.can_write));
        setEmail(data.email);
        setIsMfaEnabled(data.is_mfa_enabled);
        setCanManageElevatedPermissions(!!data.can_manage_elevated_permissions);
        setUserId(data.id);
        setUserType(userType);
        setAccessLevel(accessLevel);
        setUsersOrganizations(data.organizations.map(fromApi));
        setEvents(events);
        setIsLoading(false);

        mixpanel.identify(data.id);

        const currentOrganizationName = data.organizations.find(o => o.id === data.current_organization_id).name;
        const currentOrganizationId = data.organizations.find(o => o.id === data.current_organization_id).id;
        setSelectedOrganization(currentOrganizationName);
        const isIntercomDefined = typeof window.Intercom === 'function';
        if (userHasSwitchedOrgId) {
          mixpanel.track('Login', {
            'Organization Name': currentOrganizationName,
            organization_id: currentOrganizationId,
            distinct_id: data.id,
            'Login by switching org': true,
            clerk_id: clerkUser?.id,
            'User Type': data.type,
          });
        }
        if (!isIntercomDefined) return;

        window.Intercom('boot', {
          api_base: 'https://api-iam.eu.intercom.io',
          app_id: process.env.INTERCOM_APP_ID,
          name: null,
          user_id: data.id,
          user_hash: data.user_hash,
          email: data.email,
          company: {
            name: currentOrganizationName,
            id: currentOrganizationId,
          },
          companies: data.organizations,
        });
      } catch (e) {
        logout(e);
      }
    },
    [logout, clerkUser],
  );

  const changeOrganization = useCallback(
    async organizationId => {
      try {
        const userHasSwitchedOrgId = usersCurrentOrganizationId && usersCurrentOrganizationId !== organizationId;
        setIsLoading(true);
        setOrganizationHeaders(organizationId);
        setUsersCurrentOrganizationId(organizationId);
        window.localStorage.setItem('x-onomondo-org-id', organizationId);
        mixpanel.register({
          organization_id: organizationId,
        });
        getMe(userHasSwitchedOrgId);
      } catch (e) {
        logout(e);
      }
    },
    [getMe, logout, usersCurrentOrganizationId],
  );

  const getOrganizations = useCallback(async () => {
    try {
      const now = new Date();
      const isUserRecentlyCreated = clerkUser?.createdAt && now - clerkUser.createdAt < TEN_SECONDS_IN_MS;
      if (isUserRecentlyCreated) {
        await new Promise(resolve => setTimeout(resolve, FIVE_SECONDS_IN_MS));
      }

      const clerkToken = await clerkGetToken({ template: process.env.CLERK_DEFAULT_TOKEN_TEMPLATE });
      const { data } = await api.get('/me/organizations', {
        headers: {
          authorization: `Bearer ${clerkToken}`,
        },
      });
      const organizations = data.organizations.map(o => ({ id: o?.id, name: o?.entity }));
      setIsRequestAccessAvailable(data.is_request_access_available);
      setUsersOrganizations(organizations);
      return organizations;
    } catch (e) {
      logout(e);
      return [];
    }
  }, [logout, clerkGetToken, clerkUser?.createdAt]);

  useEffect(() => {
    const canSetOrganization = !isLoading || isSignedIn;
    const hasOrganizationSet = !!usersCurrentOrganizationId;
    const hasOnlyOneOrganizationAvailable = usersOrganizations.length === 1;
    if (!canSetOrganization || hasOrganizationSet || !hasOnlyOneOrganizationAvailable) return;

    changeOrganization(usersOrganizations[0].id);
  }, [isSignedIn, usersCurrentOrganizationId, usersOrganizations, changeOrganization, isLoading]);

  useEffect(() => {
    if (!isLoaded) return;
    async function initAuthData() {
      try {
        if (!isSignedIn) return setIsLoading(false);
        const cachedOrganizationUuid = window.localStorage.getItem('x-onomondo-org-id') ?? null;

        setInterceptors(history, clerkGetToken);
        const organizations = await getOrganizations();
        const organizationIdToUse = cachedOrganizationUuid || (organizations.length === 1 && organizations[0].id);
        if (organizationIdToUse) return changeOrganization(organizationIdToUse);
        setIsLoading(false);
      } catch (e) {
        logout(e);
      }
    }
    initAuthData();
  }, [isLoaded, isSignedIn, history, clerkGetToken, getOrganizations, changeOrganization, logout]);

  useEffect(() => {
    const shouldRemoveCache = assumedOrganizationId === null;
    setIsViewingAsAssumedOrganization(false);
    if (shouldRemoveCache) {
      window.localStorage.removeItem('onomondo-organization-id');
      mixpanel.register({
        assumed_organization_id: null,
      });
    } else {
      window.localStorage.setItem('onomondo-organization-id', assumedOrganizationId);
      mixpanel.register({
        assumed_organization_id: assumedOrganizationId,
      });
    }
  }, [assumedOrganizationId]);

  const assumeOrganizationId = useCallback(id => {
    setAssumedOrganizationId(id || null);
  }, []);

  const canWriteNetworkList = networkList => {
    if (accessLevel !== 'member') return true;
    const flatWriteTagsIds = writeTags.map(wt => wt.id);
    const flatNetworkListTagsIds = networkList?.tags.map(nt => nt.id);
    const canWrite = flatWriteTagsIds.some(id => flatNetworkListTagsIds.includes(id));
    return canWrite;
  };

  async function setUserCompletedSimPageOnboarding() {
    try {
      await api.post('/events/sim-page-onboarding-flow-completed');
    } catch (e) {
      logout(e);
    }
  }

  async function requestAccess() {
    if (!isRequestAccessAvailable) return;

    await api.post('/users/request-access-email');
  }

  function toggleIsViewingAsAssumedOrganization() {
    setIsViewingAsAssumedOrganization(!isViewingAsAssumedOrganization);
  }

  return (
    <AuthenticationContext.Provider
      value={{
        logout,
        isLoading,
        isLoggedIn: isSignedIn,
        isGod: userType === 'god',
        isDemiGod: userType === 'demigod',
        isOwner: accessLevel === 'owner',
        isAdmin: accessLevel === 'admin',
        isOrgMember: accessLevel === 'member',
        accessLevel,
        userId,
        userType,
        email,
        events,
        isMfaEnabled,
        setIsMfaEnabled,
        canManageElevatedPermissions,
        writeTags,
        readTags,
        canWrite: accessLevel === 'member' ? writeTags.length > 0 : true,
        canView: accessLevel === 'member' ? readTags.length > 0 || writeTags.length > 0 : true,
        isViewingAsAssumedOrganization,
        toggleIsViewingAsAssumedOrganization,
        assumedOrganizationId,
        assumeOrganizationId,
        usersOrganizations,
        usersCurrentOrganizationId,
        isRequestAccessAvailable,
        requestAccess,
        changeOrganization,
        canWriteNetworkList,
        setUserCompletedSimPageOnboarding,
        getMe,
        clerkUserFirstName: clerkUser?.firstName,
        clerkId: clerkUser?.id,
        selectedOrganization,
      }}
      {...props}
    />
  );
}

export { AuthenticationContext, AuthenticationProvider };
